// The one way to call salvagers-api (api/). Every route but health is behind
// the shared Cognito authorizer, so every call carries the stored token;
// refusals come back as {"detail": "..."}.

import { bearerToken, loadTokens } from "./auth/session";
import { refreshSession, storeRefreshedTokens } from "./auth/refreshSession";
import { getDeploymentConfig } from "./deployment-config";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
  }
}

/**
 * An authorized call made with no stored sign-in. Not a 401: nothing was
 * sent and no session ended, so it must not clear the cache.
 */
export class SignedOutError extends ApiError {
  constructor() {
    super("Sign in to use this.");
  }
}

/** The HTTP status an API error carries, for the query client. */
export const statusOf = (error: unknown): number | undefined =>
  error instanceof ApiError ? error.status : undefined;

const authErrorListeners = new Set<() => void>();

/** Called when a request stays unauthorized after one refresh attempt. */
export function onAuthError(listener: () => void): () => void {
  authErrorListeners.add(listener);
  return () => void authErrorListeners.delete(listener);
}

async function refreshStoredTokens(): Promise<boolean> {
  const refreshToken = loadTokens()?.refreshToken;
  if (!refreshToken) return false;
  const { cognitoDomain, clientId } = getDeploymentConfig();
  const result = await refreshSession({ cognitoDomain, clientId }, refreshToken);
  if (result.outcome !== "ok") return false;
  storeRefreshedTokens(result.tokens);
  return true;
}

/** The message to show for a failed call: the API's own, or the fallback. */
export const errorText = (error: unknown, fallback: string): string =>
  error instanceof ApiError ? error.message : fallback;

export const REQUEST_TIMEOUT_MS = 8_000;

function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

function isAbort(error: unknown): boolean {
  const name = (error as { name?: string })?.name;
  if (name === "AbortError" || name === "TimeoutError") return true;
  return /abort/i.test((error as { message?: string })?.message ?? "");
}

// A timeout aborts the fetch itself rather than racing it. A cancel from the
// caller's own signal (a query being dropped) is passed through untouched.
async function send(path: string, init: RequestInit): Promise<Response> {
  const timeout = timeoutSignal(REQUEST_TIMEOUT_MS);
  const signal = init.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
  try {
    return await fetch(`${getDeploymentConfig().apiUrl}${path}`, { ...init, signal });
  } catch (error) {
    if (init.signal?.aborted) throw error;
    if (isAbort(error)) throw new ApiError("The server didn't answer in time. Try again in a moment.");
    throw new ApiError("The server is unreachable right now.");
  }
}

/** The API's `{"detail": ...}` as an ApiError, or a generic one. */
async function failure(response: Response): Promise<ApiError> {
  const detail = await response
    .json()
    .then((body: { detail?: string }) => body.detail)
    .catch(() => undefined);
  return new ApiError(detail ?? `Request failed (${response.status}).`, response.status);
}

// An expired token is a 401; a token from before a group change, a 403.
// Either is refreshed once and the call retried.
export async function apiRequest(path: string, init: RequestInit = {}, retried = false): Promise<Response> {
  const token = bearerToken(loadTokens());
  if (!token) throw new SignedOutError();
  const response = await send(path, { ...init, headers: { Authorization: `Bearer ${token}`, ...init.headers } });
  if ((response.status === 401 || response.status === 403) && !retried && (await refreshStoredTokens())) {
    return apiRequest(path, init, true);
  }
  if (response.status === 401) {
    authErrorListeners.forEach((listener) => listener());
    throw new ApiError("Your sign-in has expired. Sign in again.", 401);
  }
  if (response.status === 403) throw new ApiError("This account isn't allowed to do that.", 403);
  if (!response.ok) throw await failure(response);
  return response;
}

export const apiJson = async <T>(path: string, init: RequestInit = {}): Promise<T> =>
  (await apiRequest(path, init)).json() as Promise<T>;

/** A route with no authorizer (health, the banner, sign-ups): no token, no refresh. */
export async function publicRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const response = await send(path, init);
  if (!response.ok) throw await failure(response);
  return response;
}

export const publicJson = async <T>(path: string): Promise<T> => (await publicRequest(path)).json() as Promise<T>;

export const json = (body: unknown): RequestInit => ({
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});
